/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  AnyAction,
  createDraftSafeSelector,
  createSlice
} from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiStatus } from '../../../../ts';
import {
  Snapshot,
  SnapshotSkill,
  SnapshotSkillId
} from '../../../../ts/snapshot';
import { SnapshotsSkillsSlice } from '../../../../ts/state';
import { SET_ASSISTANT_ID } from '../session/actionTypes';
import {
  createSnapshot,
  deleteSnapshot,
  setClassicSnapshotSkillId,
  notifySnapshotResult,
  notifySnapshotStatus,
  revertSnapshot,
  assignSnapshot,
  swapSnapshot,
  exportSnapshot,
  listSnapshotsWithArgs
} from './actions';
import { entityAdapter } from './adapter';

// Initial State
const initialState = entityAdapter.getInitialState({
  listStatus: {}, // idle, pending, fulfilled, error
  listFetched: {},
  listError: {},
  classicSnapshotSkillId: '',
  assignStatus: {},
  assignError: {},
  createStatus: {},
  createError: {},
  deleteStatus: {},
  deleteError: {},
  exportStatus: {},
  exportError: {},
  revertStatus: {},
  revertError: {},
  swapStatus: {},
  swapError: {}
});

/**
 * Gets the initial state of the redux slice and apply updates.
 * @param {*} updates The updates to apply to the initial state
 * @returns {*} The slice initial state
 */
export const computeState = (updates?: any): SnapshotsSkillsSlice => ({
  ..._.cloneDeep(initialState),
  ...updates
});

type DraftSnapshotsSelector = (
  state: SnapshotsSkillsSlice,
  skillId: string
) => Snapshot[];

/**
 * Selector that can be used in a reducer and returns the list of snapshots for a skill.
 */
const draftSafeSnapshots: DraftSnapshotsSelector = createDraftSafeSelector(
  [state => state.entities, (_state, skillId) => skillId],
  (entities, skillId): Snapshot[] => entities[skillId]?.snapshots || []
) as (state: SnapshotsSkillsSlice, skillId: string) => Snapshot[];

const upsertSnapshot = (
  state: SnapshotsSkillsSlice,
  { skillId, snapshot }: SnapshotSkill
) => {
  // fetch the current copy of the skillSnapshot in redux
  const currentSkillSnapshot = state.entities?.[skillId];
  // normal upsert will function to add the first entity (prior list action not required)
  if (!currentSkillSnapshot) {
    const snapshots = [snapshot];
    state.ids.push(skillId);
    state.entities[skillId] = { skillId, snapshots };
  } else {
    let currentSnapshots = draftSafeSnapshots(state, skillId);
    if (!currentSnapshots) {
      currentSnapshots = [];
    } else {
      // it's an array but immer protected, so make a writable copy
      //  (writing directly will cause an immer calculate-next-draft-state infinite loop)
      currentSnapshots = Array.from(currentSnapshots);
    }
    const existingEntryIndex = currentSnapshots.findIndex(
      entry => entry.snapshot === snapshot.snapshot
    );
    if (existingEntryIndex < 0) {
      currentSnapshots.push(snapshot);
    } else {
      currentSnapshots[existingEntryIndex] = snapshot;
    }
    state.entities[skillId].snapshots = currentSnapshots;
  }
};

const removeOneSnapshot = (
  state: SnapshotsSkillsSlice,
  { skillId, snapshotId }: SnapshotSkillId
) => {
  const currentSnapshots = draftSafeSnapshots(state, skillId);
  // filter out the entry that has been deleted
  const nextSnapshots = currentSnapshots.filter(
    entry => entry.snapshot !== snapshotId
  );
  state.entities[skillId].snapshots = nextSnapshots;
};

// Slice + Reducer
const slice = createSlice({
  name: 'snapshots',
  initialState: computeState(),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(notifySnapshotResult, (state, action) => {
        upsertSnapshot(state, action.payload);
      })
      .addCase(notifySnapshotStatus, (state, action) => {
        upsertSnapshot(state, action.payload);
      })
      .addCase(setClassicSnapshotSkillId, (state, action) => {
        state.classicSnapshotSkillId = action.payload;
      })
      .addCase(listSnapshotsWithArgs.pending, (state, action) => {
        const { skillId: id } = action.meta?.arg;
        state.listStatus[id] = ApiStatus.LOADING;
      })
      .addCase(listSnapshotsWithArgs.fulfilled, (state, action) => {
        const id = action.payload.skillId;
        state.listStatus[id] = ApiStatus.SUCCEEDED;
        state.listFetched[id] = Date.now();
        state.listError[id] = null;
        entityAdapter.upsertOne(state, action.payload);
      })
      .addCase(listSnapshotsWithArgs.rejected, (state, action) => {
        const { skillId: id } = action.meta?.arg;
        state.listStatus[id] = ApiStatus.ERROR;
        state.listError[id] = action.error;
      })
      .addCase(createSnapshot.pending, (state, action) => {
        const id = action.meta?.arg?.skillId;
        state.createStatus[id] = ApiStatus.LOADING;
        state.createError[id] = null;
      })
      .addCase(createSnapshot.fulfilled, (state, action) => {
        const id = action.payload.skillId;
        state.createStatus[id] = ApiStatus.SUCCEEDED;
        state.createError[id] = null;
        upsertSnapshot(state, action.payload);
      })
      .addCase(createSnapshot.rejected, (state, action) => {
        const id = action.meta?.arg?.skillId;
        state.createStatus[id] = ApiStatus.ERROR;
        state.createError[id] = action.error;
      })
      .addCase(deleteSnapshot.pending, (state, action) => {
        const id = action.meta?.arg?.skillId;
        state.deleteStatus[id] = ApiStatus.LOADING;
        state.deleteError[id] = null;
      })
      .addCase(deleteSnapshot.fulfilled, (state, action) => {
        const id = action.payload.skillId;
        state.deleteStatus[id] = ApiStatus.SUCCEEDED;
        state.deleteError[id] = null;
        removeOneSnapshot(state, action.payload);
        // Clear the cache if a snapshot was reverted or deleted
        state.listFetched[id] = null;
      })
      .addCase(deleteSnapshot.rejected, (state, action) => {
        const id = action.meta?.arg?.skillId;
        state.deleteStatus[id] = ApiStatus.ERROR;
        state.deleteError[id] = action.error;
      })
      .addCase(revertSnapshot.pending, (state, action) => {
        const id = action.meta.arg.skillId;
        state.revertStatus[id] = ApiStatus.LOADING;
        state.revertError[id] = null;
      })
      .addCase(revertSnapshot.fulfilled, (state, action) => {
        const id = action.payload.skill_id;
        state.revertStatus[id] = ApiStatus.SUCCEEDED;
        state.revertError[id] = null;
        // Clear the cache if a snapshot was reverted or deleted
        state.listFetched[id] = null;
      })
      .addCase(revertSnapshot.rejected, (state, action) => {
        const id = action.meta.arg.skillId;
        state.revertStatus[id] = ApiStatus.ERROR;
        state.revertError[id] = action.error;
      })
      .addCase(assignSnapshot.pending, (state, action) => {
        const id = action.meta.arg.skillId;
        state.assignStatus[id] = ApiStatus.LOADING;
        state.assignError[id] = null;
      })
      .addCase(assignSnapshot.fulfilled, (state, action) => {
        const id = action.payload.skill_id;
        state.assignStatus[id] = ApiStatus.SUCCEEDED;
        state.assignError[id] = null;
        // Clear the cache if a snapshot was assigned
        state.listFetched[id] = null;
      })
      .addCase(assignSnapshot.rejected, (state, action) => {
        const id = action.meta.arg.skillId;
        state.assignStatus[id] = ApiStatus.ERROR;
        state.assignError[id] = action.error;
      })
      .addCase(swapSnapshot.pending, (state, action) => {
        const id = action.meta.arg.skillId;
        state.swapStatus[id] = ApiStatus.LOADING;
        state.swapError[id] = null;
      })
      .addCase(swapSnapshot.fulfilled, (state, action) => {
        const id = action.payload.skill_id;
        state.swapStatus[id] = ApiStatus.SUCCEEDED;
        state.swapError[id] = null;
        // Clear the cache if a snapshot was swapped
        state.listFetched[id] = null;
      })
      .addCase(swapSnapshot.rejected, (state, action) => {
        const id = action.meta.arg.skillId;
        state.swapStatus[id] = ApiStatus.ERROR;
        state.swapError[id] = action.error;
      })
      .addCase<string, AnyAction>(SET_ASSISTANT_ID, (state, action) => {
        if (action.assistantId) {
          return computeState();
        }
      })
      .addCase(exportSnapshot.pending, (state, action) => {
        const id = action.meta.arg.skillId;
        state.exportStatus[id] = ApiStatus.LOADING;
        state.exportError[id] = null;
      })
      .addCase(exportSnapshot.fulfilled, (state, action) => {
        const id = action.payload.skill_id;
        state.exportStatus[id] = ApiStatus.SUCCEEDED;
        state.exportError[id] = null;
      })
      .addCase(exportSnapshot.rejected, (state, action) => {
        const id = action.meta.arg.skillId;
        state.exportStatus[id] = ApiStatus.ERROR;
        state.exportError[id] = action.error;
      });
  }
});

export default slice.reducer;
